'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Calendar,
  Clock,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Video,
  AlertCircle,
  Users,
  XCircle,
  Globe,
} from 'lucide-react';
import Link from 'next/link';
import { captureClientError } from '@/lib/error-reporting/client';
import { TEAM_SIZES } from '@/features/demo/types';
import type { TimeSlot, AvailableSlotsResponse, BookingResponse } from '@/features/demo/types';
import { LocaleProvider, useLocale } from '@/features/marketing/hooks/useLocale';
import type { BilingualContent, Locale } from '@/features/marketing/hooks/useLocale';
import LanguageToggle from '@/features/marketing/components/LanguageToggle';

// =============================================================================
// BILINGUAL CONTENT (RO-first — primary market is Romania; EN kept for the
// legacy English compare/case-study pages that still link here). Self-contained
// dictionary: this standalone page predates LANDING_CONTENT and content.ts is
// owned by a different lane, so copy lives here rather than the shared file.
// Voice: informal "tu", no em-dashes (same rule as the rebuilt landing).
// =============================================================================

const DEMO_CONTENT = {
  badge: { en: '60-Minute Live Demo', ro: 'Demo live de 60 de minute' },
  headlinePrefix: { en: 'See CMG Workflow CRM in ', ro: 'Vezi CMG Workflow CRM în ' },
  headlineHighlight: { en: 'Action', ro: 'acțiune' },
  subhead: {
    en: "Book a 60-minute live call with our team. We'll walk you through the platform and answer every question you have.",
    ro: 'Rezervă un apel live de 60 de minute cu echipa noastră. Îți arătăm platforma și răspundem la toate întrebările tale.',
  },
  backToHome: { en: 'Back to Home', ro: 'Înapoi acasă' },
  stepDate: { en: 'Date', ro: 'Dată' },
  stepTime: { en: 'Time', ro: 'Oră' },
  stepDetails: { en: 'Details', ro: 'Detalii' },
  loadSlotsError: {
    en: 'Unable to load available times. Please try again.',
    ro: 'Nu am putut încărca orele disponibile. Încearcă din nou.',
  },
  retry: { en: 'Retry', ro: 'Reîncearcă' },
  selectDateTitle: { en: 'Select a Date', ro: 'Alege o dată' },
  selectTimeTitle: { en: 'Select a Time', ro: 'Alege o oră' },
  changeDate: { en: 'Change date', ro: 'Schimbă data' },
  change: { en: 'Change', ro: 'Schimbă' },
  checkingAvailability: { en: 'Checking availability…', ro: 'Verificăm disponibilitatea…' },
  noSlots: {
    en: 'No available slots for this date.',
    ro: 'Nu sunt ore disponibile pentru această dată.',
  },
  tryAnotherDay: { en: 'Try selecting another day.', ro: 'Încearcă altă zi.' },
  allBooked: {
    en: 'All slots are booked for this date.',
    ro: 'Toate orele sunt ocupate pentru această dată.',
  },
  bookedTag: { en: 'Booked', ro: 'Ocupat' },
  todayLegend: { en: 'Today', ro: 'Azi' },
  unavailableLegend: { en: 'Unavailable', ro: 'Indisponibil' },
  yourInfoTitle: { en: 'Your Information', ro: 'Datele tale' },
  nameLabel: { en: 'Full Name', ro: 'Nume complet' },
  namePlaceholder: { en: 'John Doe', ro: 'Ion Popescu' },
  emailLabel: { en: 'Work Email', ro: 'Email de lucru' },
  emailPlaceholder: { en: 'john@company.com', ro: 'ion@companie.ro' },
  phoneLabel: { en: 'Phone Number', ro: 'Telefon' },
  phonePlaceholder: { en: '+1 (555) 000-0000', ro: '+40 7XX XXX XXX' },
  optionalTag: { en: '(optional)', ro: '(opțional)' },
  companyLabel: { en: 'Company Name', ro: 'Companie' },
  companyPlaceholder: { en: 'Acme Inc.', ro: 'Numele companiei tale' },
  teamSizeLabel: { en: 'Team Size', ro: 'Mărimea echipei' },
  teamSizePlaceholder: {
    en: 'How many people on your team?',
    ro: 'Câți oameni sunt în echipa ta?',
  },
  back: { en: 'Back', ro: 'Înapoi' },
  confirmBooking: { en: 'Confirm Booking', ro: 'Confirmă rezervarea' },
  booking: { en: 'Booking…', ro: 'Se rezervă…' },
  confirmedTitle: { en: 'Demo Confirmed', ro: 'Demo confirmat' },
  confirmedBody: {
    en: 'Your demo call is scheduled. Check your email for the confirmation and calendar invite.',
    ro: 'Apelul tău demo este programat. Verifică-ți emailul pentru confirmare și invitația de calendar.',
  },
  joinGoogleMeet: { en: 'Join Google Meet', ro: 'Intră pe Google Meet' },
  addToCalendar: { en: 'Add to Calendar', ro: 'Adaugă în calendar' },
  returnHome: { en: 'Return Home', ro: 'Înapoi acasă' },
  cancelBooking: { en: 'Cancel Booking', ro: 'Anulează rezervarea' },
  cancelConfirmQuestion: { en: 'Cancel this demo?', ro: 'Anulezi acest demo?' },
  yesCancel: { en: 'Yes, cancel', ro: 'Da, anulează' },
  cancelling: { en: 'Cancelling…', ro: 'Se anulează…' },
  noKeepIt: { en: 'No, keep it', ro: 'Nu, păstrează-l' },
  cancelledTitle: { en: 'Demo Cancelled', ro: 'Demo anulat' },
  cancelledBody: {
    en: 'Your demo has been cancelled and every attendee was notified. Book a new one anytime.',
    ro: 'Demo-ul tău a fost anulat și toți participanții au fost anunțați. Poți rezerva unul nou oricând.',
  },
  bookAnotherDemo: { en: 'Book Another Demo', ro: 'Rezervă alt demo' },
  errorSlotTaken: {
    en: 'This time slot was just taken. Please select another time.',
    ro: 'Ora aleasă tocmai a fost ocupată. Alege alt interval.',
  },
  errorBookingGeneric: {
    en: 'Something went wrong. Please try again.',
    ro: 'Ceva nu a mers bine. Încearcă din nou.',
  },
  errorCancelFailed: {
    en: 'Failed to cancel booking. Please try again or contact us.',
    ro: 'Anularea rezervării a eșuat. Încearcă din nou sau contactează-ne.',
  },
  calendarEventTitle: { en: 'CMG Workflow CRM Live Demo', ro: 'Demo live CMG Workflow CRM' },
  calendarEventDetailsNoLink: {
    en: 'A calendar invite with the meeting link has been sent to your email.',
    ro: 'O invitație de calendar cu linkul întâlnirii a fost trimisă pe emailul tău.',
  },
  calendarEventDetailsWithLinkPrefix: { en: 'Join the demo call:', ro: 'Intră la apelul demo:' },
} as const satisfies Record<string, BilingualContent>;

const TEAM_SIZE_LABELS: Record<string, BilingualContent> = {
  '1-5': { en: '1-5 people', ro: '1-5 oameni' },
  '6-20': { en: '6-20 people', ro: '6-20 oameni' },
  '21-50': { en: '21-50 people', ro: '21-50 oameni' },
  '51-200': { en: '51-200 people', ro: '51-200 oameni' },
  '200+': { en: '200+ people', ro: 'peste 200 oameni' },
};

/** Fallback value sent to the API when the visitor leaves an optional field
 * blank — the server contract (bookingSchema in api/demo/book/route.ts) still
 * requires non-empty company/teamSize strings, so we keep the payload valid
 * without touching that route (out of this lane's scope). */
const UNSPECIFIED: BilingualContent = { en: 'Not specified', ro: 'Nespecificat' };

// =============================================================================
// TIMEZONE UTILITIES
// =============================================================================

/** Format a UTC ISO string to "HH:MM" in a given timezone */
function formatUtcInTz(utcIso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(utcIso));
    // eslint-disable-next-line sonarjs/no-ignored-exceptions -- intentional: invalid timezone → fallback to raw UTC slice; pure utility with clear fallback return
  } catch (_) {
    // Fallback if timezone is invalid
    return new Date(utcIso).toISOString().slice(11, 16);
  }
}

/** Curated timezone list — covers all major regions */
const TIMEZONE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST)' },
  { value: 'America/Denver', label: 'Mountain Time (MST)' },
  { value: 'America/Chicago', label: 'Central Time (CST)' },
  { value: 'America/New_York', label: 'Eastern Time (EST)' },
  { value: 'America/Sao_Paulo', label: 'Brasilia (BRT)' },
  { value: 'Atlantic/Reykjavik', label: 'Iceland (GMT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET)' },
  { value: 'Europe/Rome', label: 'Rome (CET)' },
  { value: 'Europe/Bucharest', label: 'Bucharest (EET)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
];

/** Get the user's timezone, ensuring it exists in our list (or add it) */
function getVisitorTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
    // eslint-disable-next-line sonarjs/no-ignored-exceptions -- intentional: browser timezone detection failure → safe default; no error info useful here
  } catch (_) {
    return 'Europe/Bucharest';
  }
}

function buildGoogleCalendarUrl(
  startUtc: string,
  endUtc: string,
  locale: Locale,
  meetLink?: string
): string {
  const fmt = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d+/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: DEMO_CONTENT.calendarEventTitle[locale],
    dates: `${fmt(startUtc)}/${fmt(endUtc)}`,
    details: meetLink
      ? `${DEMO_CONTENT.calendarEventDetailsWithLinkPrefix[locale]}\n${meetLink}`
      : DEMO_CONTENT.calendarEventDetailsNoLink[locale],
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// =============================================================================
// TYPES & CONSTANTS
// =============================================================================

type Step = 'date' | 'time' | 'form' | 'confirmed';

const WEEKDAY_NAMES: Record<Locale, string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ro: ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'],
};
const MONTH_NAMES: Record<Locale, string[]> = {
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  ro: [
    'ianuarie',
    'februarie',
    'martie',
    'aprilie',
    'mai',
    'iunie',
    'iulie',
    'august',
    'septembrie',
    'octombrie',
    'noiembrie',
    'decembrie',
  ],
};

// =============================================================================
// HOOKS
// =============================================================================

function useScrollReveal(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

// =============================================================================
// SCROLL REVEAL WRAPPER
// =============================================================================

function ScrollReveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={cn(
        'opacity-0 translate-y-8 transition-all duration-700 ease-out',
        isVisible && 'opacity-100 translate-y-0',
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// =============================================================================
// GLASS INPUT (from contact page pattern)
// =============================================================================

interface GlassInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  required?: boolean;
}

function GlassInput({ label, required, className, ...props }: GlassInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <label className="block text-sm text-gray-400 mb-2 font-light">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <div className="relative">
        <input
          {...props}
          required={required}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          className={cn(
            'w-full px-4 py-3.5 rounded-xl',
            'bg-black/40 border border-white/10',
            'text-white placeholder-gray-600',
            'focus:outline-none focus:border-amber-500/50',
            'transition-all duration-300',
            isFocused && 'shadow-[0_0_20px_rgba(245,158,11,0.15)]',
            className
          )}
        />
        <div
          className={cn(
            'absolute inset-0 rounded-xl pointer-events-none',
            'bg-gradient-to-r from-amber-500/10 to-transparent',
            'opacity-0 transition-opacity duration-300',
            isFocused && 'opacity-100'
          )}
        />
      </div>
    </div>
  );
}

// =============================================================================
// GLASS SELECT (simplified for team size)
// =============================================================================

interface GlassSelectProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder: string;
}

function GlassSelect({ label, required, value, onChange, options, placeholder }: GlassSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm text-gray-400 mb-2 font-light">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          setIsFocused(true);
        }}
        className={cn(
          'w-full px-4 py-3.5 rounded-xl text-left',
          'bg-black/40 border border-white/10',
          'flex items-center justify-between gap-2',
          'transition-all duration-300',
          isFocused && 'border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.15)]',
          !value && 'text-gray-600',
          value && 'text-white'
        )}
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <ChevronRight
          className={cn(
            'w-4 h-4 text-gray-500 transition-transform duration-200',
            isOpen && 'rotate-90'
          )}
        />
      </button>

      <div
        className={cn(
          'absolute z-50 w-full mt-2 py-2 rounded-xl',
          'bg-[#0a0a0a]/95 backdrop-blur-xl',
          'border border-white/10',
          'shadow-[0_20px_60px_rgba(0,0,0,0.8)]',
          'transition-all duration-200 origin-top',
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
            className={cn(
              'w-full px-4 py-3 text-left',
              'flex items-center justify-between gap-2',
              'hover:bg-amber-500/10 transition-colors',
              value === option.value ? 'text-amber-500' : 'text-gray-300'
            )}
          >
            <span>{option.label}</span>
            {value === option.value && <CheckCircle className="w-4 h-4" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// CALENDAR DATE PICKER
// =============================================================================

function CalendarPicker({
  selectedDate,
  onSelectDate,
}: {
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}) {
  const { locale, t } = useLocale();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 30);

  // Generate calendar grid
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const startDay = firstDayOfMonth.getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const days: Array<{ day: number; dateStr: string; isSelectable: boolean; isToday: boolean }> = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(viewYear, viewMonth, d);
    const dayOfWeek = date.getDay();
    const isWeekday = dayOfWeek !== 0 && dayOfWeek !== 6;
    const isInRange = date >= today && date <= maxDate;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    days.push({
      day: d,
      dateStr,
      isSelectable: isWeekday && isInRange,
      isToday: date.getTime() === today.getTime(),
    });
  }

  const canGoPrev = viewYear > today.getFullYear() || viewMonth > today.getMonth();
  const canGoNext = new Date(viewYear, viewMonth + 1, 1) <= maxDate;

  const navigateMonth = (delta: number) => {
    const newDate = new Date(viewYear, viewMonth + delta, 1);
    setViewMonth(newDate.getMonth());
    setViewYear(newDate.getFullYear());
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          disabled={!canGoPrev}
          className={cn(
            'p-2 rounded-lg transition-all duration-200',
            canGoPrev
              ? 'hover:bg-white/5 text-gray-400 hover:text-white'
              : 'text-gray-700 cursor-not-allowed'
          )}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold text-white">
          {MONTH_NAMES[locale][viewMonth]} {viewYear}
        </h3>
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          disabled={!canGoNext}
          className={cn(
            'p-2 rounded-lg transition-all duration-200',
            canGoNext
              ? 'hover:bg-white/5 text-gray-400 hover:text-white'
              : 'text-gray-700 cursor-not-allowed'
          )}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAY_NAMES[locale].map((name) => (
          <div key={name} className="text-center text-xs text-gray-500 font-medium py-2">
            {name}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the 1st */}
        {Array.from({ length: startDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {days.map(({ day, dateStr, isSelectable, isToday }) => {
          const isSelected = selectedDate === dateStr;

          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => isSelectable && onSelectDate(dateStr)}
              disabled={!isSelectable}
              className={cn(
                'relative aspect-square flex items-center justify-center rounded-xl',
                'text-sm font-medium transition-all duration-200',
                isSelectable &&
                  !isSelected &&
                  'hover:bg-amber-500/10 hover:text-amber-500 text-gray-300 cursor-pointer',
                !isSelectable && 'text-gray-700 cursor-not-allowed',
                isSelected &&
                  'bg-amber-500 text-black font-bold shadow-[0_0_20px_rgba(245,158,11,0.3)]',
                isToday && !isSelected && 'ring-1 ring-amber-500/30'
              )}
            >
              {day}
              {isToday && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-xs text-gray-500 justify-center">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-amber-500/30 ring-1 ring-amber-500/30" />
          <span>{t(DEMO_CONTENT.todayLegend)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-gray-700" />
          <span>{t(DEMO_CONTENT.unavailableLegend)}</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TIME SLOT GRID
// =============================================================================

function TimeSlotGrid({
  slots,
  visitorTz,
  onChangeTimezone,
  isLoading,
  onSelectSlot,
}: {
  slots: TimeSlot[];
  visitorTz: string;
  onChangeTimezone: (tz: string) => void;
  isLoading: boolean;
  onSelectSlot: (slot: TimeSlot) => void;
}) {
  const { t } = useLocale();
  const [tzPickerOpen, setTzPickerOpen] = useState(false);
  const tzRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tzRef.current && !tzRef.current.contains(event.target as Node)) {
        setTzPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build the timezone options list, ensuring visitor's detected TZ is included
  const tzOptions = useMemo(() => {
    const exists = TIMEZONE_OPTIONS.some((o) => o.value === visitorTz);
    if (exists) return TIMEZONE_OPTIONS;
    // Add the detected TZ at the top
    return [{ value: visitorTz, label: visitorTz.replace(/_/g, ' ') }, ...TIMEZONE_OPTIONS];
  }, [visitorTz]);

  const currentTzLabel = tzOptions.find((o) => o.value === visitorTz)?.label || visitorTz;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        <p className="text-gray-400 text-sm">{t(DEMO_CONTENT.checkingAvailability)}</p>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Clock className="w-10 h-10 text-gray-600" />
        <p className="text-gray-400 text-center">
          {t(DEMO_CONTENT.noSlots)}
          <br />
          <span className="text-gray-500 text-sm">{t(DEMO_CONTENT.tryAnotherDay)}</span>
        </p>
      </div>
    );
  }

  const allBooked = slots.every((s) => s.booked);
  if (allBooked) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Clock className="w-10 h-10 text-gray-600" />
        <p className="text-gray-400 text-center">
          {t(DEMO_CONTENT.allBooked)}
          <br />
          <span className="text-gray-500 text-sm">{t(DEMO_CONTENT.tryAnotherDay)}</span>
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Timezone selector */}
      <div className="flex items-center justify-center gap-2 mb-4" ref={tzRef}>
        <div className="relative">
          <button
            type="button"
            onClick={() => setTzPickerOpen(!tzPickerOpen)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs',
              'border transition-all duration-200',
              tzPickerOpen
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-500'
                : 'border-white/10 bg-black/30 text-gray-400 hover:text-gray-300 hover:border-white/20'
            )}
          >
            <Globe className="w-3 h-3" />
            <span>{currentTzLabel}</span>
            <ChevronRight
              className={cn(
                'w-3 h-3 transition-transform duration-200',
                tzPickerOpen && 'rotate-90'
              )}
            />
          </button>

          {/* Dropdown */}
          <div
            className={cn(
              'absolute z-50 left-1/2 -translate-x-1/2 mt-2 w-64 py-1 rounded-xl',
              'bg-[#0a0a0a]/95 backdrop-blur-xl',
              'border border-white/10',
              'shadow-[0_20px_60px_rgba(0,0,0,0.8)]',
              'max-h-64 overflow-y-auto',
              'transition-all duration-200 origin-top',
              tzPickerOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            )}
          >
            {tzOptions.map((tz) => (
              <button
                key={tz.value}
                type="button"
                onClick={() => {
                  onChangeTimezone(tz.value);
                  setTzPickerOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2.5 text-left text-xs',
                  'flex items-center justify-between',
                  'hover:bg-amber-500/10 transition-colors',
                  visitorTz === tz.value ? 'text-amber-500' : 'text-gray-400'
                )}
              >
                <span>{tz.label}</span>
                {visitorTz === tz.value && <CheckCircle className="w-3 h-3" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {slots.map((slot) => {
          const start = formatUtcInTz(slot.startUtc, visitorTz);
          const end = formatUtcInTz(slot.endUtc, visitorTz);

          return (
            <button
              key={slot.startUtc}
              type="button"
              disabled={slot.booked}
              onClick={() => !slot.booked && onSelectSlot(slot)}
              className={cn(
                'px-4 py-3.5 rounded-xl text-center',
                'transition-all duration-200',
                'text-sm relative',
                slot.booked
                  ? 'bg-black/20 border border-white/5 text-gray-600 cursor-not-allowed line-through decoration-gray-600/50'
                  : [
                      'bg-black/40 border border-white/10',
                      'text-white font-medium',
                      'hover:border-amber-500/50 hover:bg-amber-500/10 hover:text-amber-500',
                      'hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]',
                    ]
              )}
            >
              <Clock
                className={cn(
                  'w-4 h-4 mx-auto mb-1.5',
                  slot.booked ? 'text-gray-700' : 'text-gray-500'
                )}
              />
              {start} - {end}
              {slot.booked && (
                <span
                  className="block text-[10px] text-gray-600 mt-1 no-underline"
                  style={{ textDecoration: 'none' }}
                >
                  {t(DEMO_CONTENT.bookedTag)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// GLASS CARD (from contact page)
// =============================================================================

function GlassCard({
  children,
  className,
  icon,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  title?: string;
}) {
  return (
    <div
      className={cn(
        'group relative p-8 rounded-[32px]',
        'border border-white/5 hover:border-amber-500/20',
        'bg-[#0a0a0a]',
        'transition-all duration-500',
        'hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)]',
        className
      )}
    >
      <div
        className={cn(
          'absolute inset-0 rounded-[32px] overflow-hidden opacity-0 group-hover:opacity-100',
          'bg-gradient-to-br from-amber-500/5 via-transparent to-transparent',
          'transition-opacity duration-500'
        )}
      />
      <div className="relative z-10">
        {(icon || title) && (
          <div className="flex items-center gap-3 mb-6">
            {icon && (
              <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-amber-500 group-hover:bg-amber-500/10 group-hover:border-amber-500/20 transition-all duration-300">
                {icon}
              </div>
            )}
            {title && <h2 className="text-lg font-semibold text-white">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// =============================================================================
// STEP INDICATOR
// =============================================================================

function StepIndicator({ currentStep }: { currentStep: Step }) {
  const { t } = useLocale();
  const steps: Array<{ key: Step; label: string; icon: React.ReactNode }> = [
    { key: 'date', label: t(DEMO_CONTENT.stepDate), icon: <Calendar className="w-4 h-4" /> },
    { key: 'time', label: t(DEMO_CONTENT.stepTime), icon: <Clock className="w-4 h-4" /> },
    { key: 'form', label: t(DEMO_CONTENT.stepDetails), icon: <Users className="w-4 h-4" /> },
  ];

  const stepOrder: Step[] = ['date', 'time', 'form', 'confirmed'];
  const currentIndex = stepOrder.indexOf(currentStep);

  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, i) => {
        const stepIndex = stepOrder.indexOf(step.key);
        const isActive = stepIndex === currentIndex;
        const isCompleted = stepIndex < currentIndex;

        return (
          <div key={step.key} className="flex items-center gap-2">
            {i > 0 && (
              <div
                className={cn(
                  'w-8 h-px transition-colors duration-300',
                  isCompleted ? 'bg-amber-500' : 'bg-white/10'
                )}
              />
            )}
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300',
                isActive && 'bg-amber-500/20 text-amber-500 border border-amber-500/30',
                isCompleted && 'bg-amber-500/10 text-amber-500/70',
                !isActive && !isCompleted && 'text-gray-600'
              )}
            >
              {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : step.icon}
              <span className="hidden sm:inline">{step.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

function DemoPageContent() {
  const { locale, t } = useLocale();
  const [step, setStep] = useState<Step>('date');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [visitorTz, setVisitorTz] = useState(() => getVisitorTimezone());
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<BookingResponse | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    teamSize: '',
  });

  // Team size options relabelled per-locale for display; underlying `value`s
  // stay whatever TEAM_SIZES (features/demo/types.ts) defines so the payload
  // sent to /api/demo/book is unaffected.
  const teamSizeOptions = useMemo(
    () =>
      TEAM_SIZES.map((size) => ({
        value: size.value,
        label: t(TEAM_SIZE_LABELS[size.value] ?? { en: size.label, ro: size.label }),
      })),
    [t]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  // Fetch available slots when a date is selected
  const fetchSlots = useCallback(
    async (date: string) => {
      setIsLoadingSlots(true);
      setError(null);
      setSlots([]);

      try {
        const response = await fetch(`/api/demo/available-slots?date=${date}`);
        const data: AvailableSlotsResponse = await response.json();

        if (!response.ok || !data.success) {
          throw new Error('Failed to fetch available slots');
        }

        setSlots(data.slots);
      } catch (err) {
        captureClientError(
          err instanceof Error ? err : new Error('Failed to fetch demo slots'),
          'client_runtime',
          { component: 'demo_page', date }
        ).catch(() => {});
        setError(t(DEMO_CONTENT.loadSlotsError));
      } finally {
        setIsLoadingSlots(false);
      }
    },
    [t]
  );

  // Handle date selection
  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep('time');
    fetchSlots(date);
  };

  // Handle slot selection
  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep('form');
    setError(null);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot || !selectedDate) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/demo/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          // Company/team size are optional in this UI, but the server contract
          // (bookingSchema in api/demo/book/route.ts) still requires non-empty
          // strings — send a placeholder rather than touching that route.
          company: formData.company.trim() || t(UNSPECIFIED),
          teamSize: formData.teamSize || t(UNSPECIFIED),
          startUtc: selectedSlot.startUtc,
          endUtc: selectedSlot.endUtc,
        }),
      });

      const data = await response.json();

      if (response.status === 409) {
        // Slot was taken — go back to time selection
        setError(t(DEMO_CONTENT.errorSlotTaken));
        setStep('time');
        if (selectedDate) fetchSlots(selectedDate);
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to book demo');
      }

      setBookingResult(data);
      setStep('confirmed');
    } catch (err) {
      captureClientError(
        err instanceof Error ? err : new Error('Demo booking failed'),
        'client_runtime',
        { component: 'demo_booking_form' }
      ).catch(() => {});
      setError(err instanceof Error ? err.message : t(DEMO_CONTENT.errorBookingGeneric));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format selected date for display
  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return new Intl.DateTimeFormat(locale === 'ro' ? 'ro-RO' : 'en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Handle cancellation
  const handleCancel = async () => {
    if (!bookingResult?.eventId) return;
    setIsCancelling(true);
    setError(null);

    try {
      const response = await fetch('/api/demo/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: bookingResult.eventId }),
      });

      if (!response.ok) {
        throw new Error('Failed to cancel booking');
      }

      setIsCancelled(true);
    } catch (err) {
      captureClientError(
        err instanceof Error ? err : new Error('Demo cancellation failed'),
        'client_runtime',
        { component: 'demo_cancel' }
      ).catch(() => {});
      setError(t(DEMO_CONTENT.errorCancelFailed));
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  // =========================================================================
  // CONFIRMATION STATE
  // =========================================================================

  if (step === 'confirmed' && bookingResult) {
    return (
      <div className="min-h-screen text-white" style={{ backgroundColor: '#050505' }}>
        <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute -top-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-green-600/20 blur-[120px]" />
          <div className="absolute -bottom-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-green-900/10 blur-[100px]" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
              `,
              backgroundSize: '48px 48px',
            }}
          />
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <ScrollReveal className="max-w-lg w-full text-center">
            {isCancelled ? (
              <>
                <div className="mb-8 inline-flex p-6 rounded-full bg-red-500/10 border border-red-500/30">
                  <XCircle className="w-16 h-16 text-red-500" />
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  {t(DEMO_CONTENT.cancelledTitle)}
                </h1>
                <p className="text-gray-400 mb-8 leading-relaxed">
                  {t(DEMO_CONTENT.cancelledBody)}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('date');
                      setIsCancelled(false);
                      setBookingResult(null);
                      setSelectedDate(null);
                      setSelectedSlot(null);
                      setError(null);
                    }}
                    className={cn(
                      'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
                      'bg-amber-500/10 border border-amber-500/30 text-amber-500',
                      'hover:bg-amber-500/20',
                      'transition-all duration-300'
                    )}
                  >
                    <Calendar className="w-4 h-4" />
                    {t(DEMO_CONTENT.bookAnotherDemo)}
                  </button>
                  <Link
                    href="/"
                    className={cn(
                      'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
                      'bg-[#0a0a0a] border border-white/10 text-white',
                      'hover:border-amber-500/30 hover:bg-[#111]',
                      'transition-all duration-300'
                    )}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t(DEMO_CONTENT.returnHome)}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="mb-8 inline-flex p-6 rounded-full bg-green-500/10 border border-green-500/30">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  {t(DEMO_CONTENT.confirmedTitle)}
                </h1>
                <p className="text-gray-400 mb-8 leading-relaxed">
                  {t(DEMO_CONTENT.confirmedBody)}
                </p>

                {/* Details card */}
                <div className="mb-8 p-6 rounded-2xl bg-[#0a0a0a] border border-white/10 text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-amber-500" />
                    <span className="text-white font-medium">{bookingResult.dateTime}</span>
                  </div>
                  <div className="flex gap-3">
                    {bookingResult.meetLink && (
                      <a
                        href={bookingResult.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl',
                          'bg-[#1a73e8] text-white font-bold',
                          'hover:bg-[#1557b0]',
                          'transition-all duration-300',
                          'text-sm'
                        )}
                      >
                        <Video className="w-5 h-5" />
                        {t(DEMO_CONTENT.joinGoogleMeet)}
                      </a>
                    )}
                    {selectedSlot && (
                      <a
                        href={buildGoogleCalendarUrl(
                          selectedSlot.startUtc,
                          selectedSlot.endUtc,
                          locale,
                          bookingResult.meetLink
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl',
                          'bg-white/5 border border-white/10 text-white font-bold',
                          'hover:border-amber-500/30 hover:bg-amber-500/10 hover:text-amber-500',
                          'transition-all duration-300',
                          'text-sm'
                        )}
                      >
                        <Calendar className="w-5 h-5" />
                        {t(DEMO_CONTENT.addToCalendar)}
                      </a>
                    )}
                  </div>
                </div>

                {/* Error banner for cancel failures */}
                {error && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex items-center justify-center gap-4">
                  <Link
                    href="/"
                    className={cn(
                      'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
                      'bg-[#0a0a0a] border border-white/10 text-white',
                      'hover:border-amber-500/30 hover:bg-[#111]',
                      'transition-all duration-300'
                    )}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t(DEMO_CONTENT.returnHome)}
                  </Link>

                  {/* Cancel confirmation flow */}
                  {showCancelConfirm ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">
                        {t(DEMO_CONTENT.cancelConfirmQuestion)}
                      </span>
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isCancelling}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg',
                          'bg-red-500/10 border border-red-500/30 text-red-400',
                          'hover:bg-red-500/20',
                          'transition-all duration-300 text-sm',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                      >
                        {isCancelling ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5" />
                        )}
                        {isCancelling ? t(DEMO_CONTENT.cancelling) : t(DEMO_CONTENT.yesCancel)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCancelConfirm(false)}
                        className="px-3 py-2 rounded-lg text-gray-500 hover:text-white text-sm transition-colors"
                      >
                        {t(DEMO_CONTENT.noKeepIt)}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowCancelConfirm(true)}
                      className={cn(
                        'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
                        'text-gray-500 hover:text-red-400',
                        'border border-transparent hover:border-red-500/20',
                        'transition-all duration-300'
                      )}
                    >
                      <XCircle className="w-4 h-4" />
                      {t(DEMO_CONTENT.cancelBooking)}
                    </button>
                  )}
                </div>
              </>
            )}
          </ScrollReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // BOOKING FLOW
  // =========================================================================

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#050505' }}>
      {/* Background atmospheric effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute -top-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-amber-600/20 blur-[120px]" />
        <div className="absolute -bottom-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-amber-900/10 blur-[100px]" />
        <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] rounded-full bg-amber-500/5 blur-[80px]" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Content wrapper */}
      <div className="relative" style={{ zIndex: 1 }}>
        {/* Header */}
        <header className="border-b border-white/5 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm">{t(DEMO_CONTENT.backToHome)}</span>
            </Link>
            <LanguageToggle />
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-32">
          {/* Hero — the 60-minute duration is stated in the badge AND the
              subhead so visitors see it before they commit to any step. */}
          <ScrollReveal className="text-center mb-10">
            <div
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6',
                'border border-amber-500/30 bg-amber-500/10'
              )}
            >
              <Video className="w-4 h-4 text-amber-500" />
              <span className="text-[10px] text-amber-500 font-mono uppercase tracking-[0.3em] font-bold">
                {t(DEMO_CONTENT.badge)}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              {t(DEMO_CONTENT.headlinePrefix)}
              <span className="text-amber-500">{t(DEMO_CONTENT.headlineHighlight)}</span>
            </h1>
            <p className="text-gray-400 max-w-xl mx-auto leading-relaxed text-sm md:text-base">
              {t(DEMO_CONTENT.subhead)}
            </p>
          </ScrollReveal>

          {/* Step indicator */}
          <StepIndicator currentStep={step} />

          {/* Error banner */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                {error}
                {step === 'time' && selectedDate && (
                  <button
                    type="button"
                    onClick={() => {
                      fetchSlots(selectedDate).catch(() => {});
                    }} // intentional: fetchSlots handles its own error state
                    className="ml-2 underline hover:text-red-300 transition-colors"
                  >
                    {t(DEMO_CONTENT.retry)}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 1: DATE PICKER */}
          {step === 'date' && (
            <ScrollReveal>
              <GlassCard
                icon={<Calendar className="w-5 h-5" />}
                title={t(DEMO_CONTENT.selectDateTitle)}
              >
                <CalendarPicker selectedDate={selectedDate} onSelectDate={handleDateSelect} />
              </GlassCard>
            </ScrollReveal>
          )}

          {/* STEP 2: TIME SLOTS */}
          {step === 'time' && (
            <ScrollReveal>
              <GlassCard
                icon={<Clock className="w-5 h-5" />}
                title={t(DEMO_CONTENT.selectTimeTitle)}
              >
                {/* Selected date summary */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-amber-500" />
                    <span className="text-gray-300">{formatSelectedDate()}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('date');
                      setError(null);
                    }}
                    className="text-xs text-gray-500 hover:text-amber-500 transition-colors"
                  >
                    {t(DEMO_CONTENT.changeDate)}
                  </button>
                </div>

                <TimeSlotGrid
                  slots={slots}
                  visitorTz={visitorTz}
                  onChangeTimezone={setVisitorTz}
                  isLoading={isLoadingSlots}
                  onSelectSlot={handleSlotSelect}
                />
              </GlassCard>
            </ScrollReveal>
          )}

          {/* STEP 3: BOOKING FORM — only name + email are required (scheduling
              essentials, alongside the date/time already chosen); phone,
              company, and team size are optional context for the call. */}
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Selected date/time summary bar */}
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-amber-500" />
                  <span className="text-gray-300">{formatSelectedDate()}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-gray-300">
                    {selectedSlot
                      ? `${formatUtcInTz(selectedSlot.startUtc, visitorTz)} - ${formatUtcInTz(selectedSlot.endUtc, visitorTz)}`
                      : ''}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStep('time');
                    setError(null);
                  }}
                  className="ml-auto text-xs text-gray-500 hover:text-amber-500 transition-colors"
                >
                  {t(DEMO_CONTENT.change)}
                </button>
              </div>

              <ScrollReveal delay={100} className="relative z-10">
                <GlassCard
                  icon={<Users className="w-5 h-5" />}
                  title={t(DEMO_CONTENT.yourInfoTitle)}
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <GlassInput
                      label={t(DEMO_CONTENT.nameLabel)}
                      required
                      name="name"
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t(DEMO_CONTENT.namePlaceholder)}
                    />
                    <GlassInput
                      label={t(DEMO_CONTENT.emailLabel)}
                      required
                      type="email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      spellCheck={false}
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t(DEMO_CONTENT.emailPlaceholder)}
                    />
                    <GlassInput
                      label={`${t(DEMO_CONTENT.phoneLabel)} ${t(DEMO_CONTENT.optionalTag)}`}
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      inputMode="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t(DEMO_CONTENT.phonePlaceholder)}
                    />
                    <GlassInput
                      label={`${t(DEMO_CONTENT.companyLabel)} ${t(DEMO_CONTENT.optionalTag)}`}
                      name="company"
                      autoComplete="organization"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t(DEMO_CONTENT.companyPlaceholder)}
                    />
                    <div className="md:col-span-2">
                      <GlassSelect
                        label={`${t(DEMO_CONTENT.teamSizeLabel)} ${t(DEMO_CONTENT.optionalTag)}`}
                        value={formData.teamSize}
                        onChange={(value) => setFormData((prev) => ({ ...prev, teamSize: value }))}
                        options={teamSizeOptions}
                        placeholder={t(DEMO_CONTENT.teamSizePlaceholder)}
                      />
                    </div>
                  </div>
                </GlassCard>
              </ScrollReveal>

              {/* Submit */}
              <ScrollReveal delay={200} className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    setStep('time');
                    setError(null);
                  }}
                  className={cn(
                    'inline-flex items-center gap-2 px-5 py-3 rounded-xl',
                    'text-gray-400 hover:text-white',
                    'border border-white/5 hover:border-white/10',
                    'transition-all duration-300 text-sm'
                  )}
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t(DEMO_CONTENT.back)}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    'group relative inline-flex items-center gap-3 px-8 py-4 rounded-xl',
                    'bg-[#1a1a1a] text-white font-bold',
                    'border border-white/10',
                    'hover:border-amber-500/30 hover:bg-[#222]',
                    'shadow-[0_0_30px_rgba(0,0,0,0.4)]',
                    'hover:shadow-[0_0_40px_rgba(245,158,11,0.2)]',
                    'transition-all duration-300',
                    'uppercase tracking-[0.15em] font-mono text-sm',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t(DEMO_CONTENT.booking)}</span>
                    </>
                  ) : (
                    <>
                      <span>{t(DEMO_CONTENT.confirmBooking)}</span>
                      <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </ScrollReveal>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DemoPage() {
  return (
    <LocaleProvider defaultLocale="ro">
      <DemoPageContent />
    </LocaleProvider>
  );
}
